/**
 * Utilities for mapping between DOM Ranges (what the browser gives us when the
 * user selects text) and flat character offsets into the rendered plain text
 * (what we persist on TextSelection.text_start/text_end).
 *
 * Pattern: walk the container's text nodes in document order, accumulating
 * character offsets. The resulting map supports bidirectional conversion.
 *
 * Usage:
 *   const map = buildTextOffsetMap(containerEl);
 *   const { start, end } = map.rangeToOffsets(range);
 *   const range = map.offsetsToRange(start, end);
 *   const plainText = map.plainText;
 */

// Build a flat mapping of text nodes → char offsets for `container`.
export function buildTextOffsetMap(container) {
  const nodes = [];
  let cursor = 0;
  const parts = [];

  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  let node = walker.nextNode();
  while (node) {
    const len = node.nodeValue.length;
    nodes.push({ node, start: cursor, end: cursor + len });
    parts.push(node.nodeValue);
    cursor += len;
    node = walker.nextNode();
  }

  const plainText = parts.join('');

  // Given a DOM node + offset inside that node, map to a flat offset.
  function pointToOffset(targetNode, targetOffset) {
    // If the point is an element rather than a text node (e.g. end-of-range
    // at a <br/>), walk to the first text node within.
    if (targetNode.nodeType !== Node.TEXT_NODE) {
      const entries = nodes.filter((n) => targetNode.contains(n.node));
      if (entries.length === 0) return null;
      // If offset is 0, we're at the start of targetNode's subtree; use first.
      // Otherwise, use the text node at position targetOffset-th child (best-effort).
      return targetOffset > 0
        ? entries[entries.length - 1].end
        : entries[0].start;
    }

    const entry = nodes.find((n) => n.node === targetNode);
    if (!entry) return null;
    return entry.start + Math.min(targetOffset, entry.end - entry.start);
  }

  function rangeToOffsets(range) {
    if (!range) return null;
    if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
      return null;
    }
    const start = pointToOffset(range.startContainer, range.startOffset);
    const end   = pointToOffset(range.endContainer,   range.endOffset);
    if (start === null || end === null) return null;
    return { start: Math.min(start, end), end: Math.max(start, end) };
  }

  // Given a flat char offset, locate the text node + local offset.
  function offsetToPoint(offset) {
    const entry = nodes.find((n) => offset >= n.start && offset <= n.end);
    if (!entry) return null;
    return { node: entry.node, offset: offset - entry.start };
  }

  function offsetsToRange(start, end) {
    const startPt = offsetToPoint(start);
    const endPt   = offsetToPoint(end);
    if (!startPt || !endPt) return null;
    const range = document.createRange();
    range.setStart(startPt.node, startPt.offset);
    range.setEnd(endPt.node, endPt.offset);
    return range;
  }

  return {
    nodes,
    plainText,
    rangeToOffsets,
    offsetsToRange,
    pointToOffset,
    offsetToPoint,
  };
}

// Expand [start, end] outward to the nearest word boundaries in `text`.
// Mirrors Word.resolve_with_expansion on the server; used to decide whether a
// partial selection resolves to a single word.
export function expandToWordBoundaries(text, start, end) {
  let s = Math.max(0, Math.min(start, text.length));
  let e = Math.max(s, Math.min(end, text.length));
  while (s > 0 && /\w/.test(text[s - 1])) s--;
  while (e < text.length && /\w/.test(text[e])) e++;
  return { start: s, end: e, word: text.slice(s, e) };
}
